import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { enrollmentAPI, SITE_NAMES, SITE_COLORS } from '../../lib/api';
import { RichTextContent } from '../ui/rich-text-content';
import { ArrowLeft, Download, FileText, Calendar } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const EnrollmentPage = ({ siteKey }) => {
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const colors = SITE_COLORS[siteKey];
  const siteName = SITE_NAMES[siteKey];

  useEffect(() => {
    const loadEnrollment = async () => {
      try {
        const response = await enrollmentAPI.get(siteKey);
        setEnrollment(response.data);
      } catch (error) {
        console.error('Error loading enrollment:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEnrollment();
  }, [siteKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!enrollment?.is_active) {
    return (
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-6" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-3xl font-bold text-[#2D3748] mb-4">Zápis momentálne neprobíhá</h1>
          <p className="text-[#64748B]">Sledujte naše aktuality pro informace o termínu zápisu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Zápis do školky"
        description={`Zápis do ${SITE_NAMES[siteKey]} v Nové Roli. Termíny, potřebné dokumenty, formuláře ke stažení a informace o průběhu zápisu.`}
        keywords={['zápis', 'přihláška', 'zápis do školky', 'termín zápisu']}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div 
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold text-white"
              style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
            >
              <Calendar size={14} />
              Zápis probíhá
            </div>
          </div>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">
            {enrollment.title || 'Zápis'}
          </h1>
        </div>

        {/* Poster */}
        {enrollment.poster_url && (
          <div className="mb-8 rounded-3xl overflow-hidden border-3 border-[#F0F4F8] shadow-lg">
            <img 
              src={enrollment.poster_url} 
              alt="Plakátek zápisu" 
              className="w-full h-auto object-contain"
              data-testid="enrollment-poster"
            />
          </div>
        )}

        {/* Content */}
        {enrollment.content && (
          <div className="bg-white rounded-3xl border-3 border-[#F0F4F8] p-8 lg:p-12 mb-8" data-testid="enrollment-content">
            <RichTextContent html={enrollment.content} />
          </div>
        )}

        {/* Documents */}
        {enrollment.documents?.length > 0 && (
          <div className="bg-white rounded-3xl border-3 border-[#F0F4F8] p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6">Dokumenty ke stažení</h2>
            <div className="space-y-3">
              {enrollment.documents.map((doc, index) => (
                <a
                  key={index}
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-xl bg-[#F7F9FC] hover:bg-[#EDF2F7] transition-colors"
                  data-testid={`enrollment-doc-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary}15` }}>
                    <FileText size={24} style={{ color: colors.primary }} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#2D3748]">{doc.title}</div>
                    <div className="text-sm text-[#64748B]">PDF dokument</div>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium" style={{ color: colors.primary }}>
                    <Download size={16} />
                    Stáhnout
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="rounded-3xl p-8 text-center" style={{ background: `linear-gradient(135deg, ${colors.primary}15, ${colors.secondary}15)` }}>
          <h3 className="text-xl font-bold text-[#2D3748] mb-2">Máte dotazy k zápisu?</h3>
          <p className="text-[#64748B] mb-4">Neváhejte nás kontaktovat</p>
          <Link 
            to={`/${siteKey}/kontakt`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium hover:opacity-90 transition-opacity"
            style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
            data-testid="enrollment-contact-cta"
          >
            Kontaktujte nás
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentPage;
